import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import '../Components/modal.css';
import axios from 'axios';

import Select from 'react-select';
import './edit.css';
import { useAuth } from '../hooks/AuthProvider';
import Spinner from '../Components/Spinner';
import { countries } from '../lib/countries';

import { LanguageContext } from '../context/LanguageContext';
import userTranslations from '../languages/user.json';
import Alert from '../Components/Alert';
import { getErrorMessage } from '../utils/errorUtils';
import { AddNewIcon, NameIcon } from '../svg/UtilsIcons';
import { EmailIcon, RoleIcon } from '../svg/UserIcon';

const PhoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 2H10C10.5523 2 11 2.44772 11 3V13C11 13.5523 10.5523 14 10 14H6C5.44772 14 5 13.5523 5 13V3C5 2.44772 5.44772 2 6 2Z"
      stroke="#00C0C7"
      strokeWidth="1.2"
    />
    <path
      d="M7 3.5H9"
      stroke="#00C0C7"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="8" cy="11.5" r="0.6" fill="#00C0C7" />
  </svg>
);

const EXCLUDED_ROLES = ['Owner', 'Super'];
const defaultCountry =
  countries.find((c) => c.name === 'United States') || { name: 'United States', code: '+1', position: '' };

const getCurrentCountry = (countryName) => {
  return countries.find((c) => c.name === countryName) || defaultCountry;
};

const getCountryFromPhoneInput = (phoneValue, preferredCountryName) => {
  const normalizedPhone = String(phoneValue || '').replace(/[^\d+]/g, '');

  if (!normalizedPhone.startsWith('+')) {
    return null;
  }

  const matches = countries.filter((country) => normalizedPhone.startsWith(country.code));
  if (matches.length === 0) {
    return null;
  }

  const sortedMatches = [...matches].sort((a, b) => b.code.length - a.code.length);
  const preferredMatch = sortedMatches.find((country) => country.name === preferredCountryName);
  if (preferredMatch) {
    return preferredMatch;
  }

  const defaultMatch = sortedMatches.find((country) => country.name === defaultCountry.name);
  return defaultMatch || sortedMatches[0];
};

const getCountryFlagStyle = (country) => {
  if (country?.flagImage) {
    return {
      backgroundImage: `url(${country.flagImage})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    };
  }

  return {
    backgroundPosition: country?.position || '0px 0px',
  };
};

export const EditUser = ({
  bg,
  fg,
  ft,
  btnPrimary,
  btnSecondary,
  state,
  setState,
  refresh
}) => {
  const { lang } = useContext(LanguageContext);
  const t = userTranslations[lang].editUser;
  const isRtl = ['ar', 'ur'].includes(lang);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right'
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? 'left' : 'right'
      });
    },
    [isRtl]
  );

  const [details, setDetails] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    country: ''
  });
  const [originalRole, setOriginalRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const countryMenuRef = useRef(null);

  const [allRoles, setAllRoles] = useState(['User']);

  const { permissions, user } = useAuth();
  const isSelf = Number(state.id) === Number(user?.id);
  const canEditUser = permissions['edit_users'];
  const canEditProfile = Boolean(canEditUser) || isSelf;

  const isProtectedRole = ['owner', 'super'].includes(
    String(details.role || '').toLowerCase()
  );
  const roleEditable = !!canEditUser && !isProtectedRole;

  const fetchRoles = useCallback(async () => {
    if (!canEditUser) {
      setAllRoles(['User']);
      return;
    }

    try {
      const res = await axios.get(apiUrl(`roles`));
      const roleNames = Array.isArray(res.data?.data) ? res.data.data.map((r) => r.name) : [];
      const filtered = roleNames.filter((name) => !EXCLUDED_ROLES.includes(name));
      const safe = filtered.length > 0 ? filtered : ['User'];
      setAllRoles(safe);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      const fallback = ['Admin', 'Staff', 'User'].filter((name) => !EXCLUDED_ROLES.includes(name));
      setAllRoles(fallback.length > 0 ? fallback : ['User']);
    }
  }, [canEditUser]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isCountryOpen) return;
      if (countryMenuRef.current && !countryMenuRef.current.contains(event.target)) {
        setIsCountryOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCountryOpen]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(apiUrl(`user/get/${state.id}`));

        if (res.data && res.data.status === 'OK') {
          setDetails(res.data.data);
          setOriginalRole(res.data.data?.role || '');
        } else {
          throw new Error('Received an invalid response from the server.');
        }
      } catch (err) {
        console.error('Failed to fetch user details:', err);
        const errorMessage = getErrorMessage(err);
        showAlert('error', errorMessage);
        setTimeout(() => setState({ ...state, open: false }), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (state.id) {
      fetchUserDetails();
    }
  }, [state.id, showAlert, setState]);


  const handleChange = e => {
    setDetails(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePhoneChange = useCallback((phoneValue) => {
    setDetails((prev) => {
      const matchedCountry = getCountryFromPhoneInput(phoneValue, prev.country);
      return {
        ...prev,
        phone: phoneValue,
        country: matchedCountry ? matchedCountry.name : prev.country,
      };
    });
  }, []);

  const selectCountry = () => {
    setIsCountryOpen((prev) => !prev);
  };

  const selectCountryItem = (e, country) => {
    e.stopPropagation();
    if (!country?.name || !country?.code) return;
    setDetails(prev => ({
      ...prev,
      country: country.name,
      phone: country.code
    }));
    setIsCountryOpen(false);
  };

  const saveChanges = async () => {
    if (!canEditProfile) {
      showAlert(
        'error',
        'You do not have permission to perform this action.'
      );
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const payloadDetails = { ...details };
      if (!roleEditable) {
        payloadDetails.role = originalRole;
      }

      const res = await axios.put(
        apiUrl(`user/update/${state.id}`),
        payloadDetails,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (res.status === 200 && res.data.status === 'OK') {
        showAlert('success', 'User updated successfully!');
        refresh();
        setState({ ...state, open: false });
      } else {
        throw new Error('Received an unexpected response from the server.');
      }
    } catch (err) {
      console.error('User update failed:', err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };


  if (!canEditProfile) {
    return (
      <div
        id="modal"
        style={{
          direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
        }}
      >
        <div
          id="popup"
          className="dashboard-modal"
          style={{
            animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
            fontSize:
              'calc(var(--body-font-size) * var(--font-size-multiplier))'
          }}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-icon">
              <AddNewIcon />
            </div>
            <div className="dashboard-header-text">
              <h2>{t.permissionTitle || 'Permission Denied'}</h2>
              <p>
                {t.permissionMessage ||
                  'You do not have the required permission to edit users.'}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content dashboard-content" />

          <div className="modal-footer dashboard-footer">
            <button
              className="btn btn-cancel"
              onClick={() => setState({ ...state, open: false })}
            >
              {t.buttonClose || 'Close'}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }


  const roleOptions = allRoles.map((roleName) => ({ value: roleName, label: roleName }));
  const selectedRoleOption =
    roleOptions.find((o) => o.value === details.role) ||
    (details.role ? { value: details.role, label: details.role } : null);
  const currentCountry = getCountryFromPhoneInput(details.phone, details.country) || getCurrentCountry(details.country);

  return (
    <div
      id="modal"
      style={{
        direction: lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr'
      }}
    >
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        id="popup"
        className="dashboard-modal"
        style={{
          animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
          fontSize:
            'calc(var(--body-font-size) * var(--font-size-multiplier))'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="dashboard-header">
          <div className="dashboard-header-icon">
            <AddNewIcon />
          </div>
          <div className="dashboard-header-text">
            <h2>{t.title || 'User Profile'}</h2>
            <p>{t.subtitle || t.description || ''}</p>
          </div>
        </div>

        <div className="modal-divider" />

        {loading ? (
          <div
            className="modal-content dashboard-content"
            style={{ minHeight: 200, display: 'flex', alignItems: 'center' }}
          >
            <Spinner />
          </div>
        ) : (
          <div className="modal-content dashboard-content">
            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <NameIcon />
                </span>
                <span className="form-section-title-text">
                  {t.labelName || 'Name'}
                </span>
              </div>

              <div className="form-item">
                <div className="field-with-counter">
                  <input
                    type="text"
                    className="add-column"
                    name="name"
                    placeholder={t.labelName || 'Name'}
                    maxLength={50}
                    value={details.name || ''}
                    onChange={handleChange}
                  />
                  <span className="field-counter">
                    {(details.name?.length || 0)} / 50
                  </span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <EmailIcon />
                </span>
                <span className="form-section-title-text">
                  {t.labelEmail || 'Email'}
                </span>
              </div>

              <div className="form-item">
                <input
                  type="email"
                  name="email"
                  className="add-column"
                  placeholder={t.labelEmail || 'Email'}
                  value={details.email || ''}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <RoleIcon />
                </span>
                <span className="form-section-title-text">
                  {t.labelRole || 'Role'}
                </span>
              </div>

              <div className="form-item">
                {roleEditable ? (
                  <Select
                    id="edit-user-role"
                    options={roleOptions}
                    value={selectedRoleOption}
                    onChange={(selectedOption) =>
                      setDetails((prev) => ({
                        ...prev,
                        role: selectedOption ? selectedOption.value : prev.role
                      }))
                    }
                    classNamePrefix="adduser-select"
                    isClearable={false}
                  />
                ) : (
                  <>
                    <div
                      className="add-column"
                      style={{
                        padding: '6px 12px',
                        cursor: 'not-allowed',
                        backgroundColor: 'var(--header-hover)',
                        border: '1px solid var(--modal-border)',
                        opacity: 0.9
                      }}
                      title={
                        isProtectedRole
                          ? 'This role cannot be edited.'
                          : 'You do not have permission to edit user roles.'
                      }
                    >
                      {details.role}
                    </div>

                    {(!!canEditUser && isProtectedRole) || !canEditUser ? (
                      <span className="edit-helper" style={{ marginTop: '10px' }}>
                        {isProtectedRole
                          ? t.roleProtectedMessage || 'Owner/Super roles cannot be edited.'
                          : t.rolePermissionMessage || 'You do not have permission to edit roles.'}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">
                <span className="form-section-icon">
                  <PhoneIcon />
                </span>
                <span className="form-section-title-text">
                  {t.labelPhoneNumber || 'Phone number'}
                </span>
              </div>

              <div className="form-item">
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="phone"
                    className="add-column"
                    style={{ paddingInlineStart: '64px' }}
                    placeholder={t.phonePlaceholder || '+1(222)333-44-55'}
                    value={details.phone || ''}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                  />

                  <div
                    className="edit-select-country"
                    title={t.selectCountryTitle}
                    ref={countryMenuRef}
                    onClick={selectCountry}
                  >
                    <div className="edit-country-flag" style={getCountryFlagStyle(currentCountry)}></div>
                    <span className={`edit-arrow ${isCountryOpen ? 'selected' : ''}`}></span>
                    <ul
                      className={`edit-country-list ${isCountryOpen ? 'selected' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {countries.map((country, idx) => (
                        <li
                          className="edit-country-list-item"
                          key={`${country.name}-${country.code}-${idx}`}
                          onClick={(e) => selectCountryItem(e, country)}
                        >
                          <span style={getCountryFlagStyle(country)} className="edit-country-flag"></span>
                          <span className="edit-country-name">
                            {country.name}
                          </span>
                          <span className="edit-country-code">
                            {country.code}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <span className="edit-helper" style={{ marginTop: '12px' }}>
                  {t.helperPhoneConsent.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer dashboard-footer">
          <button
            className="btn btn-cancel"
            onClick={() => setState({ ...state, open: false })}
          >
            {t.buttonCancel || 'Cancel'}
          </button>
          <button
            className="btn btn-apply dashboard-primary-btn"
            onClick={saveChanges}
          >
            {t.buttonSave || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
